import React, { useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import {
  closeModalSafely,
  handleSubmit,
} from './create-or-edit-address.service';
import ModalSheet from '../modal-sheet/modal-sheet.component';
import {
  setIsCreateBillingAddressModalOpen,
  setIsCreateDeliveryAddressModalOpen,
  setIsEditBillingAddressModalOpen,
  setIsEditDeliveryAddressModalOpen,
  setIsCartModalOpen,
  setIsSelectBillingAddressModalOpen,
} from '../../../../redux/modal/modal.actions';
import { AvForm, AvInput } from 'availity-reactstrap-validation';
import { Alert, Col, Row } from 'reactstrap';
import FormField from '../../form/fields/FormField';
import CountryField from '../../form/fields/CountryField';
import SaveSubmitButton from '../../form/buttons/SaveSubmitButton';
import {
  fetchBillingAddresses,
  fetchDeliveryAddresses,
  fetchOnAuthenticated,
} from '../../../../redux/me/me.actions';
import { setCheckout } from '../../../../redux/store/store.actions';
import { SocketContext } from '../../../../context/socket.context';
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-places-autocomplete';
import {
  extractAddressInfo,
  fetchLocation,
} from '../../../../utility/util/util.service';
import TelFormField from '../../form/fields/TelFormField';
import { useLocation, useHistory } from 'react-router-dom';
import * as Icon from 'react-feather';

// https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s21.html
const EU_VAT_ID = /^((AT)?U[0-9]{8}|(BE)?0[0-9]{9}|(BG)?[0-9]{9,10}|(CY)?[0-9]{8}L|(CZ)?[0-9]{8,10}|(DE)?[0-9]{9}|(DK)?[0-9]{8}|(EE)?[0-9]{9}|(EL|GR)?[0-9]{9}|(ES)?[0-9A-Z][0-9]{7}[0-9A-Z]|(FI)?[0-9]{8}|(FR)?[0-9A-Z]{2}[0-9]{9}|(GB)?([0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3})|(HU)?[0-9]{8}|(IE)?[0-9]S[0-9]{5}L|(IT)?[0-9]{11}|(LT)?([0-9]{9}|[0-9]{12})|(LU)?[0-9]{8}|(LV)?[0-9]{11}|(MT)?[0-9]{8}|(NL)?[0-9]{9}B[0-9]{2}|(PL)?[0-9]{10}|(PT)?[0-9]{9}|(RO)?[0-9]{2,10}|(SE)?[0-9]{12}|(SI)?[0-9]{8}|(SK)?[0-9]{10})$/i;

const CreateOrEditAddressModal = ({
  isCreateBillingAddressModalOpen,
  createBillingAddressModalData,
  isEditBillingAddressModalOpen,
  editBillingAddressModalData,
  isCreateDeliveryAddressModalOpen,
  createDeliveryAddressModalData,
  isEditDeliveryAddressModalOpen,
  editDeliveryAddressModalData,
  me,
  store,
  hasDeliveryQuery,

  openedFromOrderInfoModal,
  setIsCreateBillingAddressModalOpen,
  setIsEditBillingAddressModalOpen,
  setIsCreateDeliveryAddressModalOpen,
  setIsEditDeliveryAddressModalOpen,
  fetchBillingAddresses,
  fetchDeliveryAddresses,
  setIsCartModalOpen,
  setCheckout,
  fetchOnAuthenticated,
  setIsSelectBillingAddressModalOpen,
}) => {
  const [targetAddress, setTargetAddress] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const socketContext = useContext(SocketContext);
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const history = useHistory();
  const [displayAddressValue, setDisplayAddressValue] = useState(null);
  const formRef = useRef(null);

  const intl = useIntl();
  const isBillingAddressMode =
    isCreateBillingAddressModalOpen || isEditBillingAddressModalOpen;
  const isDeliveryAddressMode =
    isCreateDeliveryAddressModalOpen || isEditDeliveryAddressModalOpen;
  const isEditMode =
    isEditBillingAddressModalOpen || isEditDeliveryAddressModalOpen;
  const isOpen = isBillingAddressMode || isDeliveryAddressMode;
  const { billingAddress, deliveryAddress } =
    editBillingAddressModalData ||
    createBillingAddressModalData ||
    editDeliveryAddressModalData ||
    createDeliveryAddressModalData ||
    {};

  const openedCartModal = isDeliveryAddressMode
    ? createDeliveryAddressModalData?.openCartModal
    : createBillingAddressModalData?.openCartModal;

  const closeModal = () => {
    // close all (even all states get reset in redux anyway)
    setIsCreateBillingAddressModalOpen(false);
    setIsEditBillingAddressModalOpen(false);
    setIsCreateDeliveryAddressModalOpen(false);
    setIsEditDeliveryAddressModalOpen(false);
  };

  const google = window.google;

  const modalTitleId = isBillingAddressMode
    ? isEditMode
      ? 'editBillingAddressTitle'
      : 'createNewBillingAddressTitle'
    : isEditMode
    ? 'editDeliveryAddressTitle'
    : 'createNewDeliveryAddressTitle';

  useEffect(() => {
    if (isOpen) {
      // reset modal on opening
      if (store && (deliveryAddress || billingAddress)) {
        const address = deliveryAddress || billingAddress;
        setDisplayAddressValue(() => {
          const addressParts = [];

          if (address?.line1) {
            addressParts.push(address?.line1);
          }
          if (address?.line2) {
            addressParts.push(address?.line2);
          }
          if (address?.zip) {
            addressParts.push(address?.zip);
          }
          if (address?.city) {
            addressParts.push(address?.city);
          }
          if (address?.country) {
            addressParts.push(address?.country);
          }
          const displayAddress = addressParts.join(' ');

          return displayAddress;
        });
      }
      setTargetAddress(billingAddress || deliveryAddress || {});
    } else {
      // reset modal on closing
      setTargetAddress(null);
    }
  }, [isOpen]);

  const inputRef = useRef(null);

  const handleInputBlur = () => {
    // Simulate a mouse click on the first suggestion element
    const firstSuggestion = document.querySelector('.suggestion-item');
    if (firstSuggestion) {
      firstSuggestion.click();
    }
  };

  useEffect(() => {
    if (query.has('deliveryAddress') && isCreateDeliveryAddressModalOpen) {
      const deliveryAddressString = query.get('deliveryAddress');
      const deliveryAddressDecoded = decodeURIComponent(deliveryAddressString);
      const deliveryAddress = JSON.parse(deliveryAddressDecoded);
      setTargetAddress((prevState) => {
        return {
          ...prevState,
          ...deliveryAddress,
        };
      });
    }
  }, [isOpen]);

  const _closeModalSafely = () => closeModalSafely(isSubmitting, closeModal);
  const _handleSubmit = (event, errors, values) => {
    const removeSearchQuery = () => {
      history.replace({ search: null });
    };
    handleSubmit(
      event,
      errors,
      values,
      targetAddress,
      isBillingAddressMode,
      isEditMode,
      setIsSubmitting,
      setTargetAddress,
      _closeModalSafely,
      (data) => {
        isBillingAddressMode
          ? fetchBillingAddresses(
              openedFromOrderInfoModal || openedCartModal,
              openedCartModal
                ? setIsCartModalOpen
                : setIsSelectBillingAddressModalOpen,
            )
          : fetchDeliveryAddresses(openedCartModal, setIsCartModalOpen);

        isBillingAddressMode
          ? setCheckout({ billingAddressId: data.id, billingAddress: data })
          : setCheckout({ deliveryAddressId: data.id, deliveryAddress: data });
      },
      me,
      fetchOnAuthenticated,
      intl,
      socketContext.setToken,
      removeSearchQuery,
    );
  };

  const handleAddressChange = (address) => {
    setDisplayAddressValue(address);
  };

  const handleSelect = async (line1) => {
    try {
      const location = await fetchLocation(line1);

      const extractedAddress = extractAddressInfo(location?.data);

      setTargetAddress((prevState) => ({
        ...prevState,
        line1:
          extractedAddress?.street && extractedAddress?.streetNumber
            ? extractedAddress?.street + ' ' + extractedAddress?.streetNumber
            : extractedAddress?.street,
        coordinates: {
          lat: location?.data?.location?.latitude,
          lng: location?.data?.location?.longitude,
        },
        city: extractedAddress?.city,
        country: extractedAddress?.country,
        zip: extractedAddress?.zip,
        state: extractedAddress?.state,
      }));
      setDisplayAddressValue(() => {
        const addressParts = [];

        if (extractedAddress?.street) {
          addressParts.push(extractedAddress.street);
        }
        if (extractedAddress?.streetNumber) {
          addressParts.push(extractedAddress.streetNumber);
        }
        if (extractedAddress?.zip) {
          addressParts.push(extractedAddress.zip);
        }
        if (extractedAddress?.city) {
          addressParts.push(extractedAddress.city);
        }
        if (extractedAddress?.country) {
          addressParts.push(extractedAddress.country);
        }
        const displayAddress = addressParts.join(' ');

        return displayAddress;
      });
    } catch (error) {}
  };

  const maxDeliveryRadius = store?.merchantProfile?.deliveryFees
  ? Math.max(...store.merchantProfile.deliveryFees.map(fee => fee.toRadius))
  : null;

  return (
    <ModalSheet
      onClose={() => {
        setTargetAddress({});
        setDisplayAddressValue(null);
      }}
      isOpen={isOpen}
      closeModal={_closeModalSafely}
      title={intl.formatMessage({ id: modalTitleId })}
      footer={
        <div className="align-content-center">
          <SaveSubmitButton
            isSubmitting={isSubmitting}
            onClick={() => formRef?.current.handleSubmit()}
          />
        </div>
      }
    >
      <div className="px-1 pt-1">
        <div>
          {hasDeliveryQuery && (
            <Alert
              className={`p-1 mb-2 py-lg-1 d-block d-md-flex`}
              color="warning"
            >
              <span className="align-self-center">
                <Icon.AlertCircle size={20} className="warning" />
              </span>
              <span className="mr-md-2 align-self-center">
                {intl.formatMessage(
                  {
                    id: 'preFilledDeliveryAddressText',
                  },
                  { storeName: store?.name },
                )}
              </span>
            </Alert>
          )}
          <AvForm
            ref={formRef}
            onSubmit={_handleSubmit}
            disabled={isSubmitting}
          >
            {isEditMode && (
              <AvInput type="text" name="id" value={targetAddress?.id} hidden />
            )}
            {isBillingAddressMode && (
              <Row>
                <Col xs={6} className="pr-50">
                  <FormField
                    autocomplete="organization"
                    propertyName="companyName"
                    object={targetAddress}
                  />
                </Col>
                <Col xs={6} className="pl-50">
                  <FormField
                    propertyName="vatId"
                    object={targetAddress}
                    validate={{ pattern: { value: EU_VAT_ID } }}
                  />
                </Col>
              </Row>
            )}
            {isDeliveryAddressMode && (
              <Row>
                <Col xs={12}>
                  <FormField
                    autocomplete="organization"
                    propertyName="companyName"
                    object={targetAddress}
                    disabled={hasDeliveryQuery && targetAddress?.companyName}
                  />
                </Col>
              </Row>
            )}
            <Row>
              <Col xs={6} className="pr-50">
                <FormField
                  autocomplete="given-name"
                  propertyName="firstName"
                  object={targetAddress}
                  disabled={hasDeliveryQuery && targetAddress?.firstName}
                  required
                />
              </Col>
              <Col xs={6} className="pl-50">
                <FormField
                  autocomplete="family-name"
                  propertyName="lastName"
                  disabled={hasDeliveryQuery && targetAddress?.lastName}
                  object={targetAddress}
                  required
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <div className="form-group">
                  <PlacesAutocomplete
                    messageId="formFieldLine1"
                    value={displayAddressValue}
                    onChange={handleAddressChange}
                    onSelect={handleSelect}
                    debounce={500}
                    googleCallbackName="initMaps1"
                    searchOptions={{
                      location:
                        google?.maps &&
                        new google.maps.LatLng(
                          store?.address?.coordinates?.lat,
                          store?.address?.coordinates?.lng,
                        ),
                      types: ['address'],
                      radius: isDeliveryAddressMode
                        ? maxDeliveryRadius
                        : 200,
                    }}
                    shouldFetchSuggestions={
                      targetAddress?.line1?.length > 4 ||
                      displayAddressValue?.length > 4
                    }
                    disabled={hasDeliveryQuery && targetAddress?.line1}
                  >
                    {({
                      getInputProps,
                      suggestions,
                      getSuggestionItemProps,
                    }) => (
                      <div>
                        <FormField
                          ref={(el) => {
                            inputRef.current = el;
                          }}
                          autocomplete="street-address"
                          messageId="formFieldLine1"
                          propertyName="shownAddress"
                          object={targetAddress}
                          required
                          {...getInputProps({
                            placeholder: intl.formatMessage({
                              id: 'formFieldLine1',
                            }),
                            onBlur: handleInputBlur,
                          })}
                          disabled={hasDeliveryQuery && targetAddress?.line1}
                        />
                        <div className="autocomplete-dropdown-container">
                          {suggestions?.map((suggestion) => (
                            <div
                              key={suggestion.description}
                              {...getSuggestionItemProps(suggestion)}
                              className="suggestion-item py-50"
                            >
                              {suggestion.description}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </PlacesAutocomplete>
                </div>
                <FormField
                  propertyName="line1"
                  object={targetAddress}
                  hidden={true}
                />
              </Col>
            </Row>

            <FormField
              propertyName="line2"
              object={targetAddress}
              disabled={hasDeliveryQuery && targetAddress?.line2}
            />
            <FormField
              autocomplete="postal-code"
              propertyName="zip"
              object={targetAddress}
              disabled={hasDeliveryQuery && targetAddress?.zip}
              hidden
            />
            <FormField
              autocomplete="address-level2"
              propertyName="city"
              object={targetAddress}
              disabled={hasDeliveryQuery && targetAddress?.city}
              hidden
            />
            <CountryField
              autocomplete="country"
              object={targetAddress}
              disabled={hasDeliveryQuery && targetAddress?.country}
              hidden
            />
            <FormField
              autocomplete="address-level1"
              propertyName="state"
              object={targetAddress}
              disabled={hasDeliveryQuery && targetAddress?.state}
              hidden
            />
            {isBillingAddressMode && (
              <Row>
                <Col xs={12} sm={6} className="pr-sm-50">
                  <FormField
                    type="email"
                    autocomplete="email"
                    propertyName="email"
                    object={targetAddress}
                    required
                  />
                </Col>
                <Col xs={12} sm={6} className="pl-sm-50">
                  <TelFormField
                    propertyName="tel"
                    autocomplete="tel"
                    object={targetAddress}
                    required
                  />
                </Col>
              </Row>
            )}
            {isDeliveryAddressMode && (
              <Row>
                <Col xs={12} sm={12}>
                  <TelFormField
                    propertyName="tel"
                    autocomplete="tel"
                    object={targetAddress}
                    disabled={hasDeliveryQuery && targetAddress?.tel}
                    required
                  />
                </Col>
              </Row>
            )}
          </AvForm>
        </div>
      </div>
    </ModalSheet>
  );
};

const mapStateToProps = (state) => ({
  isCreateBillingAddressModalOpen: state.modals.isCreateBillingAddressModalOpen,
  createBillingAddressModalData: state.modals.createBillingAddressModalData,
  isEditBillingAddressModalOpen: state.modals.isEditBillingAddressModalOpen,
  editBillingAddressModalData: state.modals.editBillingAddressModalData,
  isCreateDeliveryAddressModalOpen:
    state.modals.isCreateDeliveryAddressModalOpen,
  createDeliveryAddressModalData: state.modals.createDeliveryAddressModalData,
  isEditDeliveryAddressModalOpen: state.modals.isEditDeliveryAddressModalOpen,
  editDeliveryAddressModalData: state.modals.editDeliveryAddressModalData,
  me: state.me.me,
  openedFromOrderInfoModal:
    state.modals.createBillingAddressModalData?.openedFromOrderInfo,
  store: state.store.store,
  hasDeliveryQuery:
    state.modals.createDeliveryAddressModalData?.hasDeliveryQuery,
});

export default connect(mapStateToProps, {
  setIsCreateBillingAddressModalOpen,
  setIsEditBillingAddressModalOpen,
  setIsCreateDeliveryAddressModalOpen,
  setIsEditDeliveryAddressModalOpen,
  fetchBillingAddresses,
  fetchDeliveryAddresses,
  setIsCartModalOpen,
  setCheckout,
  fetchOnAuthenticated,
  setIsSelectBillingAddressModalOpen,
})(CreateOrEditAddressModal);
